import React, { useState, useEffect } from 'react';
import { Package, LogOut, AlertTriangle, Download, Map, TrendingUp, Bell, Truck, CheckCircle, DollarSign, Clock, Shield } from 'lucide-react';

import PhotoCapture from './components/PhotoCapture';
import LiveMap from './components/LiveMap';
import TrustBadge from './components/TrustBadge';
import NotificationBell from './components/NotificationBell';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import StorekeeperView from './components/StorekeeperView';
import DispatcherView from './components/DispatcherView';
import DriverView from './components/DriverView';
import ReceiverView from './components/ReceiverView';

// ─── Storage Hook ───────────────────────────────────────────────────────────

const useStorage = () => {
  const [data, setData] = useState({
    batches: [],
    dispatches: [],
    receipts: [],
    incidents: [],
    notifications: [],
    analytics: { transporterScores: {} },
    users: [
      { id: 1, name: 'John Keeper',   role: 'storekeeper', pin: '1111', trustScore: 98 },
      { id: 2, name: 'Mary Dispatch', role: 'dispatcher',  pin: '2222', trustScore: 95 },
      { id: 3, name: 'Peter Driver',  role: 'driver',      pin: '3333', trustScore: 92 },
      { id: 4, name: 'Sarah Receiver',role: 'receiver',    pin: '4444', trustScore: 96 },
      { id: 5, name: 'Owner Boss',    role: 'manager',     pin: '5555', trustScore: 100 }
    ]
  });

  const save = (key, valueOrUpdater) => {
    setData(prev => {
      const value = typeof valueOrUpdater === 'function'
        ? valueOrUpdater(prev[key])
        : valueOrUpdater;
      const updated = { ...prev, [key]: value };
      try { localStorage.setItem('flowledger_v3', JSON.stringify(updated)); } catch (e) {}
      return updated;
    });
  };

  const addNotification = (type, message, severity = 'info') => {
    const n = { id: Date.now(), type, message, severity, timestamp: new Date().toISOString(), read: false };
    save('notifications', prev => [n, ...(prev || [])].slice(0, 50));
  };

  const updateAnalytics = (dispatchesOverride) => {
    const dispatches = dispatchesOverride || data.dispatches;
    const scores = {};
    dispatches.forEach(d => {
      if (d.transporter) {
        if (!scores[d.transporter]) scores[d.transporter] = { total: 0, incidents: 0 };
        scores[d.transporter].total++;
      }
    });
    data.incidents.forEach(inc => {
      const d = dispatches.find(x => x.id === inc.dispatchId);
      if (d?.transporter && scores[d.transporter]) scores[d.transporter].incidents++;
    });
    Object.keys(scores).forEach(t => {
      scores[t].trustScore = scores[t].total > 0
        ? Math.round(((scores[t].total - scores[t].incidents) / scores[t].total) * 100)
        : 100;
    });
    save('analytics', { ...data.analytics, transporterScores: scores });
  };

  useEffect(() => {
    try {
      const s = localStorage.getItem('flowledger_v3');
      if (s) setData(prev => ({ ...prev, ...JSON.parse(s) }));
    } catch (e) {
      console.warn('Failed to load saved data:', e);
    }
  }, []);

  return { data, save, addNotification, updateAnalytics };
};

// ─── Manager View ────────────────────────────────────────────────────────────

const ManagerView = ({ data }) => {
  const [tab, setTab] = useState('analytics');
  const [selBatch, setSelBatch] = useState(null);
  const unread = (data.notifications || []).filter(n => !n.read).length;

  if (selBatch) {
    const batch = data.batches.find(b => b.id === selBatch);
    if (!batch) { setSelBatch(null); return null; }
    const dispatches = data.dispatches.filter(d => d.batchId === batch.id);
    const receipts   = data.receipts.filter(r => dispatches.some(d => d.id === r.dispatchId));
    const incidents  = data.incidents.filter(i => dispatches.some(d => d.id === i.dispatchId));

    return (
      <div className="space-y-4">
        <button onClick={() => setSelBatch(null)} className="text-blue-600 font-semibold">← Back</button>
        <h2 className="text-xl font-bold">{batch.productName}</h2>
        <div className="bg-gray-50 p-3 rounded-lg text-sm">
          <p>Batch: {batch.id}</p>
          <p>Qty: {batch.quantity} • P {(batch.quantity * batch.unitCost).toFixed(2)}</p>
        </div>
        <div className="space-y-3">
          <div className="border-l-4 border-blue-500 pl-4 py-2">
            <p className="font-semibold">Created</p>
            <p className="text-sm text-gray-600">{new Date(batch.createdAt).toLocaleString()} — {batch.createdBy}</p>
          </div>
          {dispatches.map(d => (
            <div key={d.id} className="space-y-2">
              <div className="border-l-4 border-yellow-500 pl-4 py-2">
                <p className="font-semibold">Dispatch Prepared</p>
                <p className="text-sm">{new Date(d.preparedAt).toLocaleString()} — {d.preparedBy}</p>
              </div>
              {d.approvedAt && (
                <div className="border-l-4 border-green-500 pl-4 py-2">
                  <p className="font-semibold">Approved</p>
                  <p className="text-sm">Transporter: {d.transporter} • Driver: {d.driver}</p>
                </div>
              )}
              {d.departedAt && (
                <div className="border-l-4 border-purple-500 pl-4 py-2">
                  <p className="font-semibold">Departed</p>
                  <p className="text-sm">{new Date(d.departedAt).toLocaleString()}</p>
                </div>
              )}
              {receipts.filter(r => r.dispatchId === d.id).map(r => (
                <div key={r.id} className={`border-l-4 ${r.hasIncident ? 'border-red-500 bg-red-50' : 'border-green-500'} pl-4 py-2`}>
                  <p className="font-semibold">Received {r.hasIncident ? '⚠️' : '✅'}</p>
                  <p className="text-sm">Qty: {r.quantityReceived} • {r.condition} • {r.receivedBy}</p>
                </div>
              ))}
              {incidents.filter(i => i.dispatchId === d.id).map(inc => (
                <div key={inc.id} className="border-l-4 border-red-500 pl-4 py-2 bg-red-50">
                  <p className="font-semibold text-red-800">🚨 Incident: {inc.type}</p>
                  <p className="text-sm">Expected: {inc.quantityExpected} → Received: {inc.quantityReceived} • {inc.reason}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
        <button
          onClick={() => alert('PDF export: install jsPDF and uncomment generatePDF()')}
          className="w-full bg-blue-600 text-white p-3 rounded-lg flex items-center justify-center gap-2"
        >
          <Download className="w-5 h-5" />
          Export PDF Timeline
        </button>
      </div>
    );
  }

  const loss = data.incidents.reduce((s, i) => {
    const d = data.dispatches.find(x => x.id === i.dispatchId);
    const b = data.batches.find(x => x.id === d?.batchId);
    return s + ((i.quantityExpected - i.quantityReceived) * (b?.unitCost || 0));
  }, 0);

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Manager Dashboard</h2>

      <div className="flex gap-1 border-b overflow-x-auto">
        {[['analytics','Analytics'], ['map','Live Map'], ['notif', `Alerts${unread > 0 ? ` (${unread})` : ''}`]].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-semibold whitespace-nowrap ${tab === k ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === 'analytics' && (
        <div className="space-y-4">
          <AnalyticsDashboard data={data} />
          {loss > 0 && (
            <div className="bg-red-100 border-2 border-red-400 p-4 rounded-lg">
              <p className="font-bold text-red-800">🚨 Documented Loss</p>
              <p className="text-3xl font-bold text-red-600">P {loss.toFixed(2)}</p>
            </div>
          )}
          <div>
            <h3 className="font-bold mb-2">Product Timelines</h3>
            {data.batches.length === 0 ? (
              <p className="text-gray-400 text-center py-4">No batches yet</p>
            ) : (
              data.batches.slice(-5).reverse().map(b => (
                <div
                  key={b.id}
                  onClick={() => setSelBatch(b.id)}
                  className="border p-3 rounded-lg mb-2 cursor-pointer hover:bg-gray-50 hover:border-blue-300 transition-all"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold">{b.productName}</p>
                      <p className="text-xs text-gray-500">{b.id} • {b.status}</p>
                    </div>
                    <span className="text-xs text-blue-600 font-semibold">View →</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {tab === 'map' && <LiveMap dispatches={data.dispatches} />}

      {tab === 'notif' && (
        <div className="space-y-3">
          <h3 className="font-bold">Notifications</h3>
          {(data.notifications || []).length === 0 ? (
            <p className="text-gray-500 text-center py-8">No notifications</p>
          ) : (
            (data.notifications || []).slice(0, 10).map(n => (
              <div
                key={n.id}
                className={`border rounded-lg p-3 ${
                  n.severity === 'critical' ? 'bg-red-50 border-red-200' :
                  n.severity === 'success'  ? 'bg-green-50 border-green-200' :
                  'bg-blue-50 border-blue-200'
                }`}
              >
                <p className="font-semibold text-sm capitalize">{n.type}</p>
                <p className="text-sm mt-1">{n.message}</p>
                <p className="text-xs text-gray-500 mt-1">{new Date(n.timestamp).toLocaleString()}</p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main App ────────────────────────────────────────────────────────────────

const App = () => {
  const { data, save, addNotification, updateAnalytics } = useStorage();
  const [user, setUser] = useState(null);
  const [pin, setPin] = useState('');
  const [shake, setShake] = useState(false);

  const login = () => {
    const u = data.users.find(x => x.pin === pin);
    if (u) {
      setUser(u);
      setPin('');
    } else {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setPin('');
      alert('Wrong PIN. Try again.');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="bg-white bg-opacity-10 w-24 h-24 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-2xl">
              <Package className="w-14 h-14 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white tracking-tight">FlowLedger</h1>
            <p className="text-blue-200 text-sm mt-1">Enterprise Custody Intelligence</p>
            <div className="flex items-center justify-center gap-2 mt-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-green-300 text-xs font-semibold">LIVE SYSTEM</span>
            </div>
          </div>

          <div className={`bg-white rounded-3xl p-8 shadow-2xl transition-all ${shake ? 'scale-95' : 'scale-100'}`}>
            <p className="text-gray-600 text-sm text-center mb-4 font-semibold">Enter your 4-digit PIN</p>
            <input
              type="password"
              placeholder="••••"
              maxLength="4"
              className="w-full p-4 border-2 border-gray-200 rounded-xl text-center text-3xl tracking-widest font-bold mb-4 focus:border-blue-500 focus:outline-none"
              value={pin}
              onChange={e => setPin(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && login()}
              autoFocus
            />
            <button
              onClick={login}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-xl font-bold text-lg shadow-lg"
            >
              Login
            </button>

            <div className="mt-6 border-t pt-4">
              <p className="text-xs font-bold text-gray-500 mb-3 text-center uppercase tracking-wide">Tap to auto-fill PIN</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { role: 'Storekeeper', pin: '1111', color: 'text-blue-600' },
                  { role: 'Dispatcher',  pin: '2222', color: 'text-purple-600' },
                  { role: 'Driver',      pin: '3333', color: 'text-green-600' },
                  { role: 'Receiver',    pin: '4444', color: 'text-orange-600' },
                ].map(r => (
                  <button
                    key={r.pin}
                    onClick={() => setPin(r.pin)}
                    className="bg-gray-50 border p-2 rounded-lg text-left hover:bg-gray-100 transition-colors"
                  >
                    <p className={`text-xs font-bold ${r.color}`}>{r.role}</p>
                    <p className="text-xs text-gray-400 font-mono">PIN: {r.pin}</p>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setPin('5555')}
                className="w-full mt-2 bg-gradient-to-r from-yellow-400 to-orange-400 p-3 rounded-lg text-left hover:opacity-90 transition-opacity"
              >
                <p className="text-sm font-bold text-gray-800">👑 Manager — Full Access</p>
                <p className="text-xs text-gray-700 font-mono">PIN: 5555</p>
              </button>
            </div>
          </div>

          <p className="text-blue-300 text-xs text-center mt-4">Powered by BSTM Systems • Botswana</p>
        </div>
      </div>
    );
  }

  const roleColors = {
    storekeeper: 'from-blue-600 to-blue-700',
    dispatcher:  'from-purple-600 to-purple-700',
    driver:      'from-green-600 to-green-700',
    receiver:    'from-orange-500 to-orange-600',
    manager:     'from-yellow-500 to-orange-500'
  };

  const sharedProps = { data, save, user, addNotification };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className={`bg-gradient-to-r ${roleColors[user.role] || 'from-blue-600 to-purple-600'} text-white p-4 shadow-lg`}>
        <div className="max-w-2xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold">FlowLedger-Ω</h1>
            <p className="text-xs opacity-80">{user.name} • {user.role}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-white bg-opacity-20 px-3 py-1 rounded-full">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-xs font-semibold">Live</span>
            </div>
            <NotificationBell
              count={(data.notifications || []).filter(n => !n.read).length}
              onClick={() => {}}
            />
            <button
              onClick={() => setUser(null)}
              className="p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-all"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 pb-24">
        {user.role === 'storekeeper' && <StorekeeperView {...sharedProps} />}
        {user.role === 'dispatcher'  && <DispatcherView  {...sharedProps} updateAnalytics={updateAnalytics} />}
        {user.role === 'driver'      && <DriverView      {...sharedProps} />}
        {user.role === 'receiver'    && <ReceiverView    {...sharedProps} />}
        {user.role === 'manager'     && <ManagerView     data={data} />}
      </main>

      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg">
        <div className="max-w-2xl mx-auto px-4 py-3 flex justify-between items-center text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span>Offline-ready</span>
          </div>
          <div className="flex gap-3">
            <span>📦 {data.batches.length}</span>
            <span>🚛 {data.dispatches.filter(d => d.status === 'in_transit').length}</span>
            <span>🚨 {data.incidents.length}</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
